/**
 * Skeleton — shimmer loading placeholder.
 *
 * A soft block that pulses, with a running highlight ("блик") sweeping across:
 *  • web    — CSS keyframes injected once (compositor-only transform)
 *  • native — Animated translateX loop over an SVG white-fade gradient stripe
 *
 * Honors reduce-motion: static block, no pulse/sweep. Compose rows/cards from
 * a few of these instead of a spinner while queries load.
 */
import { useEffect, useRef, useState } from 'react'
import { Animated, Easing, Platform } from 'react-native'
import Svg, { Defs, LinearGradient, Rect, Stop } from 'react-native-svg'
import { Stack, type GetProps } from 'tamagui'

import { prefersReducedMotion } from './motion'

const SWEEP_ID = 'asl-skeleton-sweep'
const STRIPE = 90 // px width of the highlight stripe (native)

/** Inject the web keyframes exactly once. */
function ensureWebKeyframes() {
  if (Platform.OS !== 'web' || typeof document === 'undefined') return
  if (document.getElementById(SWEEP_ID)) return
  const style = document.createElement('style')
  style.id = SWEEP_ID
  style.textContent = `@keyframes ${SWEEP_ID} { 0% { transform: translateX(-100%);} 100% { transform: translateX(100%);} }`
  document.head.appendChild(style)
}

export function Skeleton({
  width = '100%',
  height = 14,
  borderRadius = 5,
  ...rest
}: GetProps<typeof Stack>) {
  const still = prefersReducedMotion()
  const [w, setW] = useState(0)
  const sweep = useRef(new Animated.Value(0)).current

  useEffect(ensureWebKeyframes, [])

  // Native sweep loop (web uses CSS keyframes instead).
  useEffect(() => {
    if (Platform.OS === 'web' || still || w <= 0) return
    const loop = Animated.loop(
      Animated.timing(sweep, { toValue: 1, duration: 1400, easing: Easing.inOut(Easing.quad), useNativeDriver: true }),
    )
    loop.start()
    return () => loop.stop()
  }, [sweep, still, w])

  const translateX = sweep.interpolate({ inputRange: [0, 1], outputRange: [-STRIPE, Math.max(w, 1)] })

  return (
    <Stack
      width={width}
      height={height}
      borderRadius={borderRadius}
      backgroundColor="$borderSecondary"
      overflow="hidden"
      onLayout={(e) => setW(e.nativeEvent.layout.width)}
      {...rest}
    >
      {!still && Platform.OS === 'web' ? (
        <div
          style={{
            position: 'absolute',
            inset: 0,
            background: 'linear-gradient(90deg, rgba(255,255,255,0) 0%, rgba(255,255,255,0.45) 50%, rgba(255,255,255,0) 100%)',
            animation: `${SWEEP_ID} 1.4s ease-in-out infinite`,
          }}
        />
      ) : null}
      {!still && Platform.OS !== 'web' && w > 0 ? (
        <Animated.View style={{ position: 'absolute', top: 0, bottom: 0, width: STRIPE, transform: [{ translateX }] }}>
          <Svg width="100%" height="100%">
            <Defs>
              <LinearGradient id="asl-sk-g" x1="0" y1="0" x2="1" y2="0">
                <Stop offset="0" stopColor="#fff" stopOpacity="0" />
                <Stop offset="0.5" stopColor="#fff" stopOpacity="0.35" />
                <Stop offset="1" stopColor="#fff" stopOpacity="0" />
              </LinearGradient>
            </Defs>
            <Rect x="0" y="0" width="100%" height="100%" fill="url(#asl-sk-g)" />
          </Svg>
        </Animated.View>
      ) : null}
    </Stack>
  )
}

/** A few text-like skeleton lines (last one shorter), for list placeholders. */
export function SkeletonLines({ lines = 3, gap = 10 }: { lines?: number; gap?: number }) {
  return (
    <Stack gap={gap}>
      {Array.from({ length: lines }, (_, i) => (
        <Skeleton key={i} height={12} width={i === lines - 1 ? '60%' : '100%'} />
      ))}
    </Stack>
  )
}
